import store from '@/store'
import { vm } from '@/main'
import { links, OppositionAllowRoles } from '@/router/links'
import { dictionaryNames, Roles, workMode } from '@/scripts/consts'

export const userAccess = async (to, from, next, pageAccess = false) => {
  if (['login', 'registration', 'registrationConfirm', 'forget', 'forgetConfirm'].includes(to.name)) {
    next()
  } else {
    await store.dispatch('user/getCurrent')
    if (!store.state.user.userId) {
      next('/login')
      vm.$toast.warning('Время сессии истекло')
    } else {
      if (pageAccess === false) next()
    }
  }
}

const goToStartPage = (from, next, message = 'Отсутствуют права на просмотр страницы', isBlockedPeriod = false) => {
  if (store.getters['user/isAdmin'] || store.getters['user/isSomeModerator'] || store.getters['user/isEmployee']) {
    next('/')
    if (from?.fullPath !== '/' || isBlockedPeriod) {
      vm.$toast.error(message)
    }
  } else if (
    store.getters['user/isEfficiency'] ||
    store.getters['user/isScientific'] ||
    store.getters['user/isAuditor']
  ) {
    next('/employee')
    if (from?.fullPath !== '/') {
      vm.$toast.error(message)
    }
  } else if (store.getters['user/isDepartManager']) {
    next('/employee-directory')
    if (from?.fullPath !== '/') {
      vm.$toast.error(message)
    }
  } else {
    next('/login')
    if (store.state.user.userId) {
      vm.$toast.error('Отсутствуют роли для просмотра страниц')
    }
  }
}

export const employeeAccess = async (to, from, next) => {
  const hasPageAccess = true
  await userAccess(to, from, next, hasPageAccess)
  const allowRoles = links.find(link => link.route === '/employee').allowRoles
  if (!store.getters['user/allUserRoles'].some(role => allowRoles.includes(role))) {
    goToStartPage(from, next, 'Отсутствуют права на просмотр страницы сотрудника')
  } else next()
}

export const allWorksAccess = async (to, from, next) => {
  const hasPageAccess = true
  await userAccess(to, from, next, hasPageAccess)
  const allowRoles = links.find(link => link.route === '/works').allowRoles
  if (!store.getters['user/allUserRoles'].some(role => allowRoles.includes(role))) {
    goToStartPage(from, next, 'Отсутствуют права на просмотр страницы добавленных работ')
  } else next()
}

export const reviewAccess = async (to, from, next) => {
  const hasPageAccess = true
  await userAccess(to, from, next, hasPageAccess)
  let allowRoles = links.find(link => link.route === '/works').allowRoles
  allowRoles = allowRoles.filter(role => role !== Roles.Employee)
  if (!store.getters['user/allUserRoles'].some(role => allowRoles.includes(role))) {
    goToStartPage(from, next, 'Отсутствуют права на просмотр страницы работ на рассмотрении')
  } else next()
}

export const declineAccess = async (to, from, next) => {
  const hasPageAccess = true
  await userAccess(to, from, next, hasPageAccess)
  let allowRoles = links.find(link => link.route === '/works').allowRoles
  allowRoles = allowRoles.filter(role => role !== Roles.Employee)
  if (!store.getters['user/allUserRoles'].some(role => allowRoles.includes(role))) {
    goToStartPage(from, next, 'Отсутствуют права на просмотр страницы отклоненных работ')
  } else next()
}

export const workAccess = async (to, from, next) => {
  if (to.params?.mode === workMode.Edit) {
    editAccess(to, from, next)
  }
  if (to.params?.mode === workMode.Show) {
    showAccess(to, from, next)
  }
  if (to.params?.mode === workMode.Process) {
    processAccess(to, from, next)
  }
}

const editAccess = async (to, from, next) => {
  const hasPageAccess = true
  await userAccess(to, from, next, hasPageAccess)
  let allowRoles = links.find(link => link.route === '/works').allowRoles
  if (!store.getters['user/allUserRoles'].some(role => allowRoles.includes(role))) {
    goToStartPage(from, next, 'Отсутствуют права на просмотр страницы создания/редактирования работ')
  } else next()
}

const showAccess = async (to, from, next) => {
  const hasPageAccess = true
  await userAccess(to, from, next, hasPageAccess)
  let allowRoles = links.find(link => link.route === '/works').allowRoles
  allowRoles.push(Roles.ViewEfficiencyMetrics, Roles.ViewScientificWork, Roles.Auditor)
  if (!store.getters['user/allUserRoles'].some(role => allowRoles.includes(role))) {
    goToStartPage(from, next, 'Отсутствуют права на просмотр страницы работы')
  } else next()
}

const processAccess = async (to, from, next) => {
  const hasPageAccess = true
  await userAccess(to, from, next, hasPageAccess)
  let allowRoles = links.find(link => link.route === '/works').allowRoles
  allowRoles = allowRoles.filter(role => role !== Roles.Employee)
  if (!store.getters['user/allUserRoles'].some(role => allowRoles.includes(role))) {
    goToStartPage(from, next, 'Отсутствуют права на просмотр страницы работы')
  } else next()
}

export const efficiencyAccess = async (to, from, next) => {
  const hasPageAccess = true
  await userAccess(to, from, next, hasPageAccess)
  let allowRoles = links.find(link => link.route === '/efficiency').allowRoles
  if (!store.getters['user/allUserRoles'].some(role => allowRoles.includes(role))) {
    goToStartPage(from, next, 'Отсутствуют права на просмотр страницы показателей эффективности')
  } else next()
}

export const publicationsAccess = async (to, from, next) => {
  const hasPageAccess = true
  await userAccess(to, from, next, hasPageAccess)
  let allowRoles = links.find(link => link.route === '/publications').allowRoles
  if (!store.getters['user/allUserRoles'].some(role => allowRoles.includes(role))) {
    goToStartPage(from, next, 'Отсутствуют права на просмотр страницы публикаций')
  } else next()
}

export const oppositionAccess = async (to, from, next) => {
  const hasPageAccess = true
  await userAccess(to, from, next, hasPageAccess)
  if (!store.getters['user/allUserRoles'].some(role => OppositionAllowRoles.includes(role))) {
    goToStartPage(from, next, 'Отсутствуют права на просмотр страницы оппонирования диссертации')
  } else next()
}

export const dictionariesAccess = async (to, from, next) => {
  const hasPageAccess = true
  await userAccess(to, from, next, hasPageAccess)
  let allowRoles = links.find(link => link.route === '/directories').allowRoles
  if (!store.getters['user/allUserRoles'].some(role => allowRoles.includes(role))) {
    goToStartPage(from, next, 'Отсутствуют права на просмотр страницы справочников')
  } else next()
}

export const employeeDirectoryAccess = async (to, from, next) => {
  const hasPageAccess = true
  await userAccess(to, from, next, hasPageAccess)
  let allowRoles = links.find(link => link.route === '/employee-directory').allowRoles
  if (!store.getters['user/allUserRoles'].some(role => allowRoles.includes(role))) {
    goToStartPage(from, next, 'Отсутствуют права на просмотр страницы справочника сотрудников')
  } else next()
}
